// Pharvlink design system — single source of truth for the light interface.
//
// Every page imports from here. Nothing hardcodes a hex value. When the
// rebrand lands, the palette changes in this file and the whole app follows.
//
//   import { c, t, radius, space, shadow, card, btn, chip } from '../../theme';
//
// Written as plain objects so they spread straight into inline styles, which
// is how this codebase already works:
//
//   <div style={{ ...card.base, padding: space.md }}>

// ── Colour ────────────────────────────────────────────────────────────────
// The greys carry a trace of green so the neutrals belong to the brand
// instead of reading as default grey.
export const c = {
  // surfaces
  canvas: '#F4F7F5',      // app background
  surface: '#FFFFFF',     // cards, sheets, bars
  surfaceAlt: '#F8FAF9',  // inset panels, striped rows
  line: '#E4EBE7',        // hairline borders
  lineStrong: '#D3DED8',  // dividers that need to be seen

  // brand
  brand: '#16A34A',       // primary actions, prices, links
  brandDeep: '#15803D',   // banners, pressed states
  brandBright: '#22C55E', // hover, highlights
  brandTint: '#ECFDF5',   // filled backgrounds
  brandTintEdge: '#BBF7D0',

  // text
  ink: '#0B1220',         // headings
  inkBody: '#334155',     // body copy
  inkMuted: '#64748B',    // secondary, captions
  inkFaint: '#94A3B8',    // placeholders, disabled

  // semantic
  danger: '#E11D48',      // short-dated urgency, destructive
  dangerTint: '#FFF5F5',
  dangerEdge: '#FECDD3',
  warn: '#D97706',        // pending verification
  warnTint: '#FFFBEB',
  info: '#0284C7',
  infoTint: '#F0F9FF',

  // fixed
  white: '#FFFFFF',
  onBrand: '#FFFFFF',     // text on brand-filled surfaces
};

// ── Spacing ───────────────────────────────────────────────────────────────
export const space = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '28px',
  section: '24px',       // gap between dashboard sections
};

// ── Radius ────────────────────────────────────────────────────────────────
export const radius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  pill: '999px',
};

// ── Elevation ─────────────────────────────────────────────────────────────
// Light and low. On a light canvas, heavy shadows read as cheap.
export const shadow = {
  card: '0 1px 2px rgba(11,18,32,0.04), 0 1px 3px rgba(11,18,32,0.05)',
  raised: '0 4px 14px rgba(11,18,32,0.08)',
  sticky: '0 1px 0 rgba(11,18,32,0.06)',
};

// ── Type ──────────────────────────────────────────────────────────────────
// System stack: renders Roboto on Android, SF on iOS, Segoe on Windows.
// Zero bytes downloaded, which matters more here than a bought typeface.
const stack =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Tabular figures. Prices, expiry dates and licence numbers all sit in
// columns people scan and compare, so the digits must be the same width.
export const numeric = {
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: "'tnum'",
};

export const t = {
  family: stack,

  // page + section headings
  pageTitle: {
    fontFamily: stack,
    fontSize: '20px',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: c.ink,
    margin: 0,
  },
  sectionTitle: {
    fontFamily: stack,
    fontSize: '16px',
    fontWeight: 800,
    letterSpacing: '-0.015em',
    color: c.ink,
    margin: 0,
  },
  cardTitle: {
    fontFamily: stack,
    fontSize: '13.5px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: c.ink,
    margin: 0,
  },

  // copy
  body: {
    fontFamily: stack,
    fontSize: '14px',
    fontWeight: 500,
    color: c.inkBody,
    margin: 0,
  },
  small: {
    fontFamily: stack,
    fontSize: '12.5px',
    fontWeight: 500,
    color: c.inkMuted,
    margin: 0,
  },
  micro: {
    fontFamily: stack,
    fontSize: '11px',
    fontWeight: 600,
    color: c.inkMuted,
    margin: 0,
  },

  // an all-caps label above a value or section
  eyebrow: {
    fontFamily: stack,
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: c.inkMuted,
    margin: 0,
  },

  // money — always tabular
  price: {
    fontFamily: stack,
    fontSize: '15px',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    color: c.brand,
    margin: 0,
    ...numeric,
  },
  priceWas: {
    fontFamily: stack,
    fontSize: '12px',
    fontWeight: 600,
    color: c.inkFaint,
    textDecoration: 'line-through',
    margin: 0,
    ...numeric,
  },
};

// ── Components ────────────────────────────────────────────────────────────
export const card = {
  base: {
    background: c.surface,
    border: '1px solid ' + c.line,
    borderRadius: radius.lg,
    boxShadow: shadow.card,
  },
  flat: {
    background: c.surface,
    border: '1px solid ' + c.line,
    borderRadius: radius.md,
  },
  inset: {
    background: c.surfaceAlt,
    border: '1px solid ' + c.line,
    borderRadius: radius.md,
  },
};

export const btn = {
  primary: {
    background: c.brand,
    color: c.onBrand,
    border: 'none',
    borderRadius: radius.md,
    padding: '12px 18px',
    fontFamily: stack,
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondary: {
    background: c.surface,
    color: c.ink,
    border: '1px solid ' + c.lineStrong,
    borderRadius: radius.md,
    padding: '11px 18px',
    fontFamily: stack,
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  quiet: {
    background: 'transparent',
    color: c.brand,
    border: 'none',
    padding: '6px 4px',
    fontFamily: stack,
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  // the round add-to-cart on a product card
  iconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: radius.sm,
    background: c.brand,
    color: c.onBrand,
    border: 'none',
    display: 'grid',
    placeItems: 'center',
    fontSize: '14px',
    cursor: 'pointer',
    flexShrink: 0,
  },
};

// Small status/label pills. `tone` picks the colour family.
export function chip(tone = 'neutral') {
  const tones = {
    neutral: { bg: c.surfaceAlt, fg: c.inkMuted, edge: c.line },
    brand: { bg: c.brandTint, fg: c.brandDeep, edge: c.brandTintEdge },
    danger: { bg: c.dangerTint, fg: c.danger, edge: c.dangerEdge },
    warn: { bg: c.warnTint, fg: c.warn, edge: '#FDE68A' },
    solid: { bg: c.brand, fg: c.onBrand, edge: c.brand },
  };
  const s = tones[tone] || tones.neutral;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: s.bg,
    color: s.fg,
    border: '1px solid ' + s.edge,
    borderRadius: radius.pill,
    padding: '3px 9px',
    fontFamily: stack,
    fontSize: '10.5px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  };
}

// Horizontal scroll rail used by every dashboard section.
export const rail = {
  display: 'flex',
  gap: '10px',
  overflowX: 'auto',
  padding: '2px 0 6px',
  scrollSnapType: 'x proximity',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
};

export const railItem = {
  flex: '0 0 auto',
  scrollSnapAlign: 'start',
};

// Page shell — every screen sits on the canvas.
//
// The wash is a single CSS gradient, not an image and not a blur: it paints
// once, costs nothing to scroll, and downloads no bytes. It fades out by
// ~380px so it reads as light falling from the header rather than a coloured
// background, and the content below it stays on a quiet surface where price
// columns and expiry dates remain easy to scan.
export const page = {
  minHeight: '100vh',
  background: c.canvas,
  backgroundImage:
    'linear-gradient(180deg, rgba(22,163,74,0.07) 0%, rgba(22,163,74,0.02) 45%, rgba(22,163,74,0) 100%)',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 380px',
  backgroundAttachment: 'local',
  fontFamily: stack,
  color: c.inkBody,
};

// Class name for the frosted bottom bar, defined in index.css because a
// backdrop-filter needs an @supports fallback and inline styles cannot
// express one. Used only on small fixed strips — never on a whole page.
export const glassClass = 'mv-glass';

// Class names that let the browser skip painting offscreen blocks.
export const railClass = 'mv-rail';
export const deferClass = 'mv-defer';
export const deferRowClass = 'mv-defer-row';

export const input = {
  width: '100%',
  boxSizing: 'border-box',
  background: c.surface,
  border: '1px solid ' + c.line,
  borderRadius: radius.md,
  padding: '12px 14px',
  fontFamily: stack,
  fontSize: '14px',
  color: c.ink,
  outline: 'none',
};

// Money, formatted the one way the whole app formats money.
export const naira = (n) =>
  n === null || n === undefined || n === ''
    ? '—'
    : '₦' + Number(n).toLocaleString('en-NG');

const theme = { c, t, space, radius, shadow, card, btn, chip, rail, railItem, page, input, naira, numeric, glassClass, railClass, deferClass, deferRowClass };
export default theme;